import { Box, Icon, Tabs } from '@cloudscape-design/components';
import type { ReactNode } from 'react';
import type { SourceAvailability } from '../common/cardLogic';

export type ScorecardSourceTabOption = {
  text: string;
  id: string;
  icon: ReactNode;
  availability: SourceAvailability;
};

export type ScorecardSourceTabsProps = {
  activeTabId: string;
  options: ScorecardSourceTabOption[];
  content: ReactNode;
  onSourceChange: (source: string) => void;
};

export function ScorecardSourceTabs({
  activeTabId,
  options,
  content,
  onSourceChange,
}: ScorecardSourceTabsProps) {
  return (
    <Tabs
      activeTabId={activeTabId}
      onChange={({ detail }) => {
        const option = options.find((candidate) => candidate.id === detail.activeTabId);
        if (!option || option.availability.disabled) return;
        onSourceChange(option.id);
      }}
      tabs={options.map((option) => ({
        label: (
          <span
            aria-disabled={option.availability.disabled ? 'true' : undefined}
            aria-label={`${option.text}: ${option.availability.state === 'data' ? 'available' : option.availability.state}`}
            className={`inline-flex items-center gap-1.5 ${
              option.availability.state === 'loading'
                ? 'text-gray-900 font-normal'
                : option.availability.state === 'unavailable'
                  ? 'opacity-50 grayscale text-gray-500'
                  : ''
            }`}
          >
            <Icon svg={option.icon} />
            <Box variant="span">{option.text}</Box>
          </span>
        ),
        id: option.id,
        // Cloudscape only renders disabledReason as a visible hover/focus
        // tooltip for a disabled tab. Loading stays normal-color via the label
        // class above while Cloudscape supplies the correct disabled semantics.
        disabled: option.availability.disabled,
        disabledReason: option.availability.disabledReason,
        content,
      }))}
    />
  );
}
